import React, { createContext, useContext, useState, ReactNode } from 'react';
import { getProfile, getSettings } from '../planner/plannerApi';
import { UserProfile, UserSettings } from '../planner/types';

interface UserInfo {
  publicId?: string;
  email?: string;
  name?: string;
  [key: string]: any;
}

interface AuthContextType {
  token: string | null;
  user: UserInfo | null;
  /** Name and photo from the server (the token only has the email); null until loaded. */
  profile: UserProfile | null;
  setProfile: (profile: UserProfile) => void;
  /**
   * The account's settings (theme, start screen, expense type). The last ones seen are kept in the browser so the
   * theme is right before the server answers; null when there are none and the server has not answered yet.
   */
  settings: UserSettings | null;
  /** True once the server answered (or failed) for this sign-in. */
  settingsLoaded: boolean;
  setSettings: (settings: UserSettings) => void;
  login: (token: string) => void;
  logout: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeJwt(token: string | null): UserInfo | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    const obj = JSON.parse(jsonPayload);
  // publicId already present if provided; no self-assignment needed
    // Fallback: if only 'sub' is present, use it as publicId
    if (!obj.publicId && obj.sub) obj.publicId = obj.sub;
    return obj;
  } catch {
    return null;
  }
}

const SETTINGS_KEY = 'userSettings';

function readSettings(): UserSettings | null {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<UserInfo | null>(() => decodeJwt(localStorage.getItem('token')));
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [settings, setSettingsState] = useState<UserSettings | null>(readSettings);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const setSettings = React.useCallback((next: UserSettings) => {
    setSettingsState(next);
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* private mode: only this tab keeps them */ }
  }, []);

  const login = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(decodeJwt(newToken));
  };
  const logout = () => {
    setToken(null);
    setUser(null);
    setProfile(null);
    setSettingsState(null);
    setSettingsLoaded(false);
    localStorage.removeItem('token');
    localStorage.removeItem(SETTINGS_KEY);
  };

  React.useEffect(() => {
    setUser(decodeJwt(token));
  }, [token]);

  // Once per sign-in: a new token after a password change keeps the same account
  const publicId = user?.publicId;
  React.useEffect(() => {
    if (!publicId) return;
    let cancelled = false;
    getProfile().then(p => { if (!cancelled) setProfile(p); }).catch(() => {});
    getSettings().then(
      s => { if (!cancelled) { setSettings(s); setSettingsLoaded(true); } },
      () => { if (!cancelled) setSettingsLoaded(true); });
    return () => { cancelled = true; };
  }, [publicId, setSettings]);

  return (
    <AuthContext.Provider value={{ token, user, profile, setProfile, settings, settingsLoaded, setSettings, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
